"use client";

import { useState, useEffect, useMemo } from "react";
import {
  Search,
  Filter,
  Download,
  X,
  ArrowUpDown,
  MoreHorizontal,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { mockSalesPipeline, salesProjects } from "@/assets/mock";
import { cn } from "@/lib/utils";

const PipelineView = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStage, setSelectedStage] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(true);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [expandedStages, setExpandedStages] = useState<Record<string, boolean>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [isFiltering, setIsFiltering] = useState(false);

  // Debounced search for smooth filtering
  useEffect(() => {
    setIsFiltering(true);
    const timer = setTimeout(() => {
      setIsFiltering(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, selectedStage]);

  // Memoized filtered pipeline for performance
  const filteredPipeline = useMemo(() => {
    const filtered = mockSalesPipeline.map(stage => ({
      ...stage,
      deals: stage.deals.filter(deal => 
        deal.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        deal.company.toLowerCase().includes(searchQuery.toLowerCase())
      )
    })).filter(stage => 
      (selectedStage === "all" || stage.id === selectedStage) &&
      stage.deals.length > 0
    );

    // Auto-expand stages when filtered
    if (searchQuery || selectedStage !== "all") {
      const autoExpanded: Record<string, boolean> = {};
      filtered.forEach(stage => {
        autoExpanded[stage.id] = true;
      });
      setExpandedStages(autoExpanded);
    }

    return filtered;
  }, [searchQuery, selectedStage]);

  const activeFiltersCount = [
    searchQuery !== "",
    selectedStage !== "all",
  ].filter(Boolean).length;

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedStage("all");
    setSortConfig(null);
    setCurrentPage(1);
    setExpandedStages({});
  };

  const getProjectByDeal = (dealName: string) => {
    return salesProjects.find(project => 
      project.name.includes(dealName) || dealName.includes(project.name)
    );
  };

  const handleSort = (key: string) => {
    setSortConfig(current => ({
      key,
      direction: current?.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key: string) => {
    if (sortConfig?.key !== key) return <ArrowUpDown className="ml-2 h-3 w-3 text-gray-400" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUpDown className="ml-2 h-3 w-3 text-gray-600 rotate-180" />
      : <ArrowUpDown className="ml-2 h-3 w-3 text-gray-600" />;
  };

  const toggleStage = (stageId: string) => {
    setExpandedStages(prev => ({
      ...prev,
      [stageId]: !prev[stageId]
    }));
  };

  const exportToExcel = () => {
    const headers = ['Stage', 'Deal Name', 'Company', 'Value', 'Status', 'Progress', 'Probability', 'Owner', 'Start Date', 'Due Date'];
    const csvContent = [
      headers.join(','),
      ...mockSalesPipeline.flatMap(stage => 
        stage.deals.map(deal => {
          const project = getProjectByDeal(deal.name);
          return [
            `"${stage.name}"`,
            `"${deal.name}"`,
            `"${deal.company}"`,
            `"${deal.value}"`,
            `"${project?.status?.replace('_', ' ') || 'N/A'}"`,
            `"${project?.progress || 'N/A'}"`,
            `"${'probability' in deal ? (deal as any).probability + '%' : 'N/A'}"`,
            `"${'owner' in deal ? (deal as any).owner : 'N/A'}"`,
            `"${project?.start_date || 'N/A'}"`,
            `"${project?.expected_completion || 'N/A'}"`
          ].join(',');
        })
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `pipeline-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Pagination logic for each stage
  const getPaginatedDeals = (deals: any[]) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return deals.slice(startIndex, startIndex + itemsPerPage);
  };

  const getTotalPages = (deals: any[]) => {
    return Math.ceil(deals.length / itemsPerPage);
  };

  // Expand all / Collapse all functionality
  const toggleAllStages = () => {
    if (Object.values(expandedStages).every(Boolean)) {
      // All are expanded, so collapse all
      setExpandedStages({});
    } else {
      // Some are collapsed, so expand all
      const allExpanded: Record<string, boolean> = {};
      filteredPipeline.forEach(stage => {
        allExpanded[stage.id] = true;
      });
      setExpandedStages(allExpanded);
    }
  };

  const allExpanded = filteredPipeline.length > 0 && filteredPipeline.every(stage => expandedStages[stage.id]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
      <div className="max-w-[1800px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-lg font-bold text-gray-900 mb-1">Sales Pipeline</h1>
            <p className="text-xs text-secondary">
              Complete sales pipeline overview with {mockSalesPipeline.reduce((acc, stage) => acc + stage.count, 0)} deals across {mockSalesPipeline.length} stages
              {isFiltering && " • Filtering..."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-white uppercase border border-secondary text-gray-700 font-normal rounded-lg text-xs transition-colors hover:border-secondary/60"
            >
              <Filter className="w-4 h-4" />
              Filters
              {activeFiltersCount > 0 && (
                <span className="bg-primary text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {activeFiltersCount}
                </span>
              )}
            </button>
            <button 
              onClick={exportToExcel}
              className="flex items-center gap-2 px-4 py-2 bg-secondary hover:bg-secondary/90 text-white font-normal rounded-lg text-xs uppercase transition-colors"
            >
              <Download className="w-4 h-4" />
              Export Pipeline Data as Excel
            </button>
          </div>
        </div>

        {/* Filters with smooth animation */}
        <div className={cn(
          "bg-white rounded-xl border border-secondary/20 overflow-hidden transition-all duration-300",
          showFilters ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        )}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-bold text-gray-900">Filter Pipeline</h3>
              <div className="flex items-center gap-2">
                {activeFiltersCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="text-xs font-semibold text-primary hover:text-primary/80 flex items-center gap-1 transition-colors"
                  >
                    <X className="w-3 h-3" />
                    Clear all
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-2 block">Search Deals</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Type to search deals or companies..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-secondary/30 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700 mb-2 block">Pipeline Stage</label>
                <select
                  value={selectedStage}
                  onChange={(e) => setSelectedStage(e.target.value)}
                  className="w-full px-3 py-2 border border-secondary/30 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 appearance-none bg-white"
                >
                  <option value="all">All Pipeline Stages</option>
                  {mockSalesPipeline.map((stage) => (
                    <option key={stage.id} value={stage.id}>
                      {stage.name} ({stage.count} deals)
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Active filters display */}
            {activeFiltersCount > 0 && (
              <div className="mt-4 p-3 bg-primary/5 rounded-lg border border-primary">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold text-primary">Active filters:</span>
                  {searchQuery && (
                    <span className="bg-primary/20 text-primary px-2 py-1 rounded-full flex items-center gap-1">
                      Search: "{searchQuery}"
                      <button onClick={() => setSearchQuery("")} className="hover:text-blue-600">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  )}
                  {selectedStage !== "all" && (
                    <span className="bg-primary/20 text-primary px-2 py-1 rounded-full flex items-center gap-1">
                      Stage: {mockSalesPipeline.find(s => s.id === selectedStage)?.name}
                      <button onClick={() => setSelectedStage("all")} className="hover:text-blue-600">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Results header */}
        <div className="flex items-center justify-between">
          <div className="text-xs text-secondary">
            Showing {filteredPipeline.length} of {mockSalesPipeline.length} stages
            {filteredPipeline.length > 0 && ` • ${filteredPipeline.reduce((acc, stage) => acc + stage.deals.length, 0)} deals found`}
          </div>
          {filteredPipeline.length > 0 && (
            <button
              onClick={toggleAllStages}
              className="text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
            >
              {allExpanded ? 'Collapse All' : 'Expand All'}
            </button>
          )}
        </div>

        {/* Pipeline Tables with smooth animations */}
        <div className="space-y-4">
          {filteredPipeline.map((stage) => {
            const totalPages = getTotalPages(stage.deals);
            const paginatedDeals = getPaginatedDeals(stage.deals);
            const isExpanded = expandedStages[stage.id];
            
            return (
              <div 
                key={stage.id} 
                className={cn(
                  "bg-white border border-secondary/20 rounded-xl overflow-hidden transition-all duration-300 hover:border-secondary/40",
                  isExpanded ? "shadow-sm" : "shadow-xs"
                )}
              >
                {/* Stage Header - Clickable with smooth animation */}
                <div 
                  className="p-5 border-b border-secondary/20 cursor-pointer transition-all duration-200 hover:bg-gray-50"
                  onClick={() => toggleStage(stage.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={cn("w-3 h-3 rounded-full transition-colors duration-200", stage.color)} />
                      <div>
                        <h3 className="text-xs font-bold text-gray-900 transition-colors">{stage.name}</h3>
                        <p className="text-xs text-secondary mt-1 transition-colors">
                          {stage.deals.length} deals • {stage.value} total value
                          {searchQuery && " • Matching your search"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-xs font-semibold text-gray-900">{stage.deals.length} deals</p>
                        <p className="text-xs text-secondary">{stage.value}</p>
                      </div>
                      <div className={cn(
                        "transition-transform duration-300",
                        isExpanded ? "rotate-180" : "rotate-0"
                      )}>
                        <ChevronDown className="w-4 h-4 text-secondary transition-colors" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stage Table - Smooth expand/collapse */}
                <div className={cn(
                  "transition-all duration-300 overflow-hidden",
                  isExpanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
                )}>
                  {paginatedDeals.length > 0 ? (
                    <>
                      <div className="overflow-x-auto custom-scrollbar">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                                <button
                                  onClick={() => handleSort('name')}
                                  className="flex items-center hover:text-gray-700 transition-colors"
                                >
                                  Deal Name
                                  {getSortIcon('name')}
                                </button>
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                                <button
                                  onClick={() => handleSort('company')}
                                  className="flex items-center hover:text-gray-700 transition-colors"
                                >
                                  Company
                                  {getSortIcon('company')}
                                </button>
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                                Value
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                                Progress
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                                Probability
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                                Owner
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedDeals.map((deal, index) => {
                              const project = getProjectByDeal(deal.name);

                              return (
                                <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-xs font-semibold text-gray-900" title={deal.name}>
                                      {truncateText(deal.name, 40)}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-xs text-secondary" title={deal.company}>
                                      {truncateText(deal.company, 25)}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-xs font-semibold text-gray-900">
                                      {deal.value}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={cn(
                                      "text-xs font-semibold capitalize px-2 py-1 rounded-full transition-colors",
                                      project?.status === 'on_track' && "bg-green-100 text-green-800",
                                      project?.status === 'at_risk' && "bg-red-100 text-red-800",
                                      project?.status === 'delayed' && "bg-amber-100 text-amber-800",
                                      project?.status === 'completed' && "bg-blue-100 text-blue-800",
                                      !project && "bg-gray-100 text-gray-800"
                                    )}>
                                      {project ? project.status.replace('_', ' ') : 'N/A'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-semibold text-gray-900">
                                        {project ? `${project.progress}%` : 'N/A'}
                                      </span>
                                      {project && (
                                        <div className="w-16 bg-gray-200 rounded-full h-2 transition-all duration-300">
                                          <div 
                                            className={cn(
                                              "h-2 rounded-full transition-all duration-500",
                                              project.status === 'on_track' && "bg-green-500",
                                              project.status === 'at_risk' && "bg-red-500",
                                              project.status === 'delayed' && "bg-amber-500",
                                              project.status === 'completed' && "bg-blue-500"
                                            )}
                                            style={{ width: `${project.progress}%` }}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={cn(
                                      "text-xs font-semibold px-2 py-1 rounded-full transition-colors",
                                      'probability' in deal && (deal as any).probability >= 80 && "bg-green-100 text-green-800",
                                      'probability' in deal && (deal as any).probability >= 60 && "bg-amber-100 text-amber-800",
                                      'probability' in deal && "bg-red-100 text-red-800",
                                      !('probability' in deal) && "bg-gray-100 text-gray-800"
                                    )}>
                                      {'probability' in deal ? `${(deal as any).probability}%` : 'N/A'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-xs text-secondary">
                                      {'owner' in deal ? (deal as any).owner : 'N/A'}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <button className="p-2 hover:bg-secondary/20 rounded-full transition-colors duration-150">
                                      <MoreHorizontal className="w-4 h-4 text-secondary" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination for each stage */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between px-6 py-4 border-t border-secondary/20 bg-gray-50">
                          <div className="flex-1 text-xs text-gray-500">
                            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, stage.deals.length)} of {stage.deals.length} entries
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              className="p-1 rounded-md bg-white border border-secondary/20 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none transition-colors duration-150"
                              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                              disabled={currentPage === 1}
                            >
                              <ChevronLeft className="h-4 w-4 text-gray-500" />
                            </button>
                            <div className="text-xs font-medium">
                              Page {currentPage} of {totalPages}
                            </div>
                            <button
                              className="p-1 rounded-md bg-white border border-secondary/20 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none transition-colors duration-150"
                              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                              disabled={currentPage === totalPages}
                            >
                              <ChevronRight className="h-4 w-4 text-gray-500" />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="px-6 py-12 text-center text-xs text-secondary">
                      <div className="flex flex-col items-center gap-2">
                        <Search className="w-12 h-12 text-gray-300" />
                        <p className="font-semibold text-gray-900">No deals found in this stage</p>
                        <p className="text-xs">Try adjusting your filters or search query</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {filteredPipeline.length === 0 && !isFiltering && (
          <div className="bg-white border border-secondary rounded-xl p-12 text-center">
            <div className="flex flex-col items-center gap-2">
              <Search className="w-12 h-12 text-gray-300" />
              <p className="font-semibold text-gray-900">No pipeline stages found</p>
              <p className="text-xs text-secondary">Try adjusting your filters or search query</p>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          height: 0.5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #FF6900;
          border-radius: 0.25px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #e55a00;
        }
      `}</style>
    </div>
  );
};

export default PipelineView;